// src/services/consolidationService.js
import api from './api';
import requestDeduplicationService from 'services/requestDeduplicationService';

class ConsolidationService {
  /**
   * Buscar dados consolidados do grupo
   */
  async buscarDadosConsolidados(companyNames, _params = {}) {
    try {
      const dataInicio = _params.data_inicio || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
      const dataFim = _params.data_fim || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];

      // Buscar dados de cada empresa
      const promises = companyNames.map(companyName => 
        this.buscarDadosEmpresa(companyName, dataInicio, dataFim)
      );

      const resultados = await Promise.all(promises);
      
      // Consolidar os dados
      const dadosConsolidados = this.consolidarDados(resultados);
      
      return dadosConsolidados;
    } catch (error) {
      console.error('❌ Erro ao buscar dados consolidados:', error);
      throw error;
    }
  }

  /**
   * Buscar dados de uma empresa específica
   */
  async buscarDadosEmpresa(companyName, dataInicio, dataFim) {
    try {
      const params = {
        empresa: companyName,
        data_inicio: dataInicio,
        data_fim: dataFim
      };


      // Usar o mesmo endpoint que o dashboard individual usa
      // Deduplicar chamadas simultâneas para a mesma empresa/intervalo
      const response = await requestDeduplicationService.executeRequest(
        async (_url, _params) => api.get('/n8n/financeiro/gerar-dashboard-completo', { params: _params }),
        '/n8n/financeiro/gerar-dashboard-completo',
        params,
        'GET',
        150000
      );
      
      if (response.data && response.data.success && response.data.data) {
        // Verificar se há estrutura aninhada (response.data.data.data)
        let dadosFinais = response.data.data;
        if (response.data.data.data) {
          dadosFinais = response.data.data.data;
        }
        
        
        return {
          companyName,
          success: true,
          data: dadosFinais[0] || dadosFinais
        };
      } else {
        console.warn(`⚠️ ConsolidationService - Dados inválidos para ${companyName}:`, response.data);
        return {
          companyName,
          success: false,
          data: null
        };
      }
    } catch (error) {
      console.error(`❌ Erro ao buscar dados da empresa ${companyName}:`, error);
      return {
        companyName,
        success: false,
        data: null
      };
    }
  }

  /**
   * Consolidar dados de múltiplas empresas
   */
  consolidarDados(resultados) {

    const dadosConsolidados = {
      total_titulos_emitidos: 0,
      valor_total_emitido: 0,
      total_titulos_recebidos: 0,
      valor_total_recebido: 0,
      total_titulos_a_receber: 0,
      valor_restante_a_receber: 0,
      empresas: [],
      dados_grafico: [],
      evolucao_mensal: []
    };

    // Mapa para consolidar dados mensais por mês
    const consolidacaoMensal = new Map();
    const dadosPorEmpresa = new Map(); // Para armazenar dados individuais de cada empresa

    resultados.forEach(resultado => {
      if (resultado.success && resultado.data) {
        const data = resultado.data;
        
        
        // Consolidar dados - verificar se tem estrutura resumo ou kpis
        let dadosParaConsolidar = null;
        
        if (data.resumo) {
          // Estrutura antiga com resumo
          dadosParaConsolidar = data.resumo;
        } else if (data.kpis) {
          // Estrutura nova com kpis do dashboard-detalhado
          dadosParaConsolidar = {
            total_titulos_emitidos: 0, // KPIs não têm esses campos
            valor_total_emitido: parseFloat(data.kpis.entradas) || 0, // Usar 'entradas' em vez de 'entradas_brutas'
            total_titulos_recebidos: 0, // KPIs não têm esses campos
            valor_total_recebido: parseFloat(data.kpis.entradas_liquidas) || 0,
            total_titulos_a_receber: 0, // KPIs não têm esses campos
            valor_restante_a_receber: parseFloat(data.kpis.caixa_liquido) || 0 // Usar 'caixa_liquido' em vez de 'saldo_liquido'
          };
        }

        if (dadosParaConsolidar) {
          
          // Somar os totais
          dadosConsolidados.total_titulos_emitidos += parseInt(dadosParaConsolidar.total_titulos_emitidos) || 0;
          dadosConsolidados.valor_total_emitido += parseFloat(dadosParaConsolidar.valor_total_emitido) || 0;
          dadosConsolidados.total_titulos_recebidos += parseInt(dadosParaConsolidar.total_titulos_recebidos) || 0;
          dadosConsolidados.valor_total_recebido += parseFloat(dadosParaConsolidar.valor_total_recebido) || 0;
          dadosConsolidados.total_titulos_a_receber += parseInt(dadosParaConsolidar.total_titulos_a_receber) || 0;
          dadosConsolidados.valor_restante_a_receber += parseFloat(dadosParaConsolidar.valor_restante_a_receber) || 0;
          // Adicionar dados da empresa
          dadosConsolidados.empresas.push({
            name: resultado.companyName,
            total_titulos_emitidos: parseInt(dadosParaConsolidar.total_titulos_emitidos) || 0,
            valor_total_emitido: parseFloat(dadosParaConsolidar.valor_total_emitido) || 0,
            total_titulos_recebidos: parseInt(dadosParaConsolidar.total_titulos_recebidos) || 0,
            valor_total_recebido: parseFloat(dadosParaConsolidar.valor_total_recebido) || 0,
            total_titulos_a_receber: parseInt(dadosParaConsolidar.total_titulos_a_receber) || 0,
            valor_restante_a_receber: parseFloat(dadosParaConsolidar.valor_restante_a_receber) || 0
          });
        }

        // Processar dados de evolução mensal por empresa
        if (data.dados && Array.isArray(data.dados)) {
          
          // Armazenar dados individuais da empresa
          dadosPorEmpresa.set(resultado.companyName, data.dados);
          
          data.dados.forEach(item => {
            const mes = item.mes;
            if (mes) {
              if (!consolidacaoMensal.has(mes)) {
                consolidacaoMensal.set(mes, {
                  mes: mes,
                  titulos_emitidos: 0,
                  valor_emitido: 0,
                  titulos_recebidos: 0,
                  valor_recebido: 0,
                  titulos_pendentes: 0,
                  valor_pendente: 0
                });
              }
              
              const consolidado = consolidacaoMensal.get(mes);
              // Usar os campos corretos para cada tipo de dado
              consolidado.titulos_emitidos += parseInt(item.quantidade_titulos_emitidos || item.quantidade_titulos_recebidos) || 0;
              consolidado.valor_emitido += parseFloat(item.valor_total_emitido || item.valor_total_recebido) || 0;
              consolidado.titulos_recebidos += parseInt(item.quantidade_titulos_recebidos) || 0;
              consolidado.valor_recebido += parseFloat(item.valor_total_recebido) || 0;
              consolidado.titulos_pendentes += parseInt(item.quantidade_titulos_pendentes || item.quantidade_titulos_a_receber) || 0;
              consolidado.valor_pendente += parseFloat(item.valor_pendente || item.valor_restante_a_receber) || 0;
            }
          });
        }
      }
    });

    // Converter mapa para array ordenado
    dadosConsolidados.evolucao_mensal = Array.from(consolidacaoMensal.values())
      .sort((a, b) => a.mes.localeCompare(b.mes));

    // Criar estrutura de dados para o gráfico com cores das empresas
    dadosConsolidados.dados_grafico = this.criarDadosGraficoComCores(dadosPorEmpresa, dadosConsolidados.evolucao_mensal);

    return dadosConsolidados;
  }

  /**
   * Criar dados do gráfico com cores das empresas
   */
  criarDadosGraficoComCores(dadosPorEmpresa, dadosConsolidados) {
    const coresEmpresas = {
      'MOV Fibra': '#8B5CF6', // Roxo e tons semelhantes
      'XT Fibra': '#DC2626', // Vermelho e tons de vermelho
      'MOV Fit': '#7C3AED' // Roxo para MOV Fit
    };

    // Cor azul para consolidado
    const corConsolidado = '#3B82F6'; // Azul

    const dadosGrafico = {
      consolidado: {
        nome: 'Consolidado',
        cor: corConsolidado,
        dados: dadosConsolidados.map(item => ({
          mes: item.mes,
          titulos_recebidos: item.titulos_recebidos,
          valor_recebido: item.valor_recebido
        }))
      },
      empresas: []
    };

    // Adicionar dados de cada empresa
    dadosPorEmpresa.forEach((dados, nomeEmpresa) => {
      const cor = coresEmpresas[nomeEmpresa] || '#6B7280';
      dadosGrafico.empresas.push({
        nome: nomeEmpresa,
        cor: cor,
        dados: dados.map(item => ({
          mes: item.mes,
          titulos_recebidos: item.quantidade_titulos_recebidos,
          valor_recebido: item.valor_total_recebido
        }))
      });
    });

    return dadosGrafico;
  }

  /**
   * Misturar duas cores para criar cor intermediária
   */
  misturarCores(cor1, cor2) {
    // Converter hex para RGB
    const hex1 = cor1.replace('#', '');
    const hex2 = cor2.replace('#', '');
    
    const r1 = parseInt(hex1.substr(0, 2), 16);
    const g1 = parseInt(hex1.substr(2, 2), 16);
    const b1 = parseInt(hex1.substr(4, 2), 16);
    
    const r2 = parseInt(hex2.substr(0, 2), 16);
    const g2 = parseInt(hex2.substr(2, 2), 16);
    const b2 = parseInt(hex2.substr(4, 2), 16);
    
    // Calcular média
    const r = Math.round((r1 + r2) / 2);
    const g = Math.round((g1 + g2) / 2);
    const b = Math.round((b1 + b2) / 2);
    
    // Converter de volta para hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
}

const consolidationService = new ConsolidationService();
export default consolidationService;
